import {
  Card,
  Dropdown,
  getClassNameFactory,
  TextBoldness,
  Typography,
} from '@dcl/ui';
import React from 'react';
import { CardData } from './types';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import ReactHtmlParser from 'react-html-parser';
import './EnhancedCard.css';

// Generate unique IDs for elements without relying on array indices
let uniqueId = 0;
const generateUniqueId = (prefix = 'item') => `${prefix}-${uniqueId++}`;

export const EnhancedCard: React.FC<{ data: CardData }> = ({ data }) => {
  const getClassName = getClassNameFactory('EnhancedCard');
  const [selectedValue, setSelectedValue] = React.useState<string>('');
  const [selectedLinkDetails, setSelectedLinkDetails] = React.useState<any>(
    null
  );

  // Create a mapping of values to link details for easy lookup
  const linkDetailsMap = React.useMemo(() => {
    const map = {};
    data.linkDetails?.forEach((link) => {
      map[link.value] = link;
    });
    return map;
  }, [data.linkDetails]);

  const handleDropdownChange = (value: string | React.ReactNode) => {
    const stringValue = String(value);
    setSelectedValue(stringValue);
    setSelectedLinkDetails(linkDetailsMap[stringValue]);
  };

  const getDropdownItems = () => {
    return (
      data.linkDetails?.map((link) => ({
        label: link.label,
        value: link.value,
      })) ?? []
    );
  };

  // Helper function to generate a key for a link
  const generateLinkKey = (link) => {
    if (link.value) {
      return `link-${link.value.replace(/[^a-zA-Z0-9]/g, '-')}`;
    }
    return generateUniqueId('link');
  };

  return (
    <Card.CardBase size="small" data-testid="EnhancedCard">
      <div className={getClassName()}>
        <div className={getClassName({ descendantName: 'headerSection' })}>
          <div className={getClassName({ descendantName: 'icon-section' })}>
            {data.icon && (
              <div className={getClassName({ descendantName: 'icon' })}>
                <img
                  src={data.icon}
                  alt={data.title ? `${data.title} icon` : 'Card icon'}
                />
              </div>
            )}

            {data.title && (
              <Typography
                tag={TypographyTags.SPAN}
                boldness={TextBoldness.Bold}
                className={getClassName({ descendantName: 'heading' })}
              >
                {data.title}
              </Typography>
            )}
          </div>

          <Typography
            tag={TypographyTags.SPAN}
            boldness={TextBoldness.Medium}
            className={getClassName({ descendantName: 'title' })}
          >
            {ReactHtmlParser(data.description ?? '')}
          </Typography>
        </div>

        <div className={getClassName({ descendantName: 'actionSection' })}>
          {data.dropdownLabel && data.dropDown && (
            <Typography
              tag={TypographyTags.SPAN}
              boldness={TextBoldness.Medium}
              className={getClassName({ descendantName: 'label' })}
            >
              {data.dropdownLabel}
            </Typography>
          )}

          {data.dropDown ? (
            <div
              className={getClassName({ descendantName: 'dropdown-container' })}
            >
              <div className={getClassName({ descendantName: 'wrapper' })}>
                <Dropdown
                  typeaheadMode
                  items={getDropdownItems()}
                  placeholder={''}
                  onValueChanged={handleDropdownChange}
                  value={selectedValue}
                  clearButtonLabel="Clear"
                  onClearButtonClick={() => {
                    setSelectedValue('');
                    setSelectedLinkDetails(null);
                  }}
                  maxVisibleItems={8}
                  block={true}
                  withCheckmark
                  className={getClassName({ descendantName: 'dropdown' })}
                />
              </div>
              <div
                className={getClassName({ descendantName: 'button-container' })}
              >
                <button
                  onClick={() => {
                    if (selectedValue && selectedLinkDetails) {
                      const shouldOpenInNewTab =
                        selectedLinkDetails.newTab === true ||
                        selectedLinkDetails.newTab === 'true';
                      window.open(
                        selectedLinkDetails.value,
                        shouldOpenInNewTab ? '_blank' : '_self'
                      );
                    }
                  }}
                  className={getClassName({
                    descendantName: 'dropdown-button',
                  })}
                >
                  {data.dropdownButtonName}
                </button>
              </div>
            </div>
          ) : (
            <div className={getClassName({ descendantName: 'link-container' })}>
              {data.linkDetails &&
                data.linkDetails.length > 0 &&
                data.linkDetails.map((link) => {
                  const linkKey = generateLinkKey(link);

                  return (
                    <a
                      key={linkKey}
                      href={link.value ?? '#'}
                      className={getClassName({ descendantName: 'link' })}
                      target={
                        link.newTab === true || link.newTab === 'true'
                          ? '_blank'
                          : '_self'
                      }
                      rel="noopener noreferrer"
                    >
                      {link.label ?? 'Learn More'}
                    </a>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </Card.CardBase>
  );
};

EnhancedCard.displayName = 'EnhancedCard';
